// ============================================
// COMPONENT - Navbar
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useLogout, useUIStore, useSession } from '@almacenes/core';
import { Button, capitalize } from '@almacenes/ui';
import { FaPowerOff, FaWindows } from 'react-icons/fa';
import { FaCrown } from 'react-icons/fa6';
import logo from '@/assets/logos/nuevo.svg';
import { goToPremium } from '@/lib/premium';

export const Navbar = () => {
  const navigate = useNavigate();
  const { session: cachedSession } = useAuthStore();
  const { data: session } = useSession();
  const isOnline = useUIStore((state) => state.isOnline);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const { mutate: logout, isPending } = useLogout();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const isDesktopApp = Boolean(window.electronAPI);

  // Usar session del servidor o caché como fallback
  const effectiveSession = session || cachedSession;

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="flex h-16 items-center justify-between bg-white px-6 shadow-sm">
      <div className="flex items-center gap-4">
        <button
          onClick={toggleSidebar}
          className="lg:hidden rounded-lg p-2 hover:bg-gray-100"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Logo para pantallas grandes */}
        <div className="hidden lg:flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" >
            {/*<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            </svg>*/}
            <img 
              src={logo} 
              alt="Almacenes Digitales" 
              className="h-9"
            />
          </div>
          <span className="font-bold" style={{ color: '#11194F' }}>
            ALMACENES<span style={{ color: '#43BFFF' }}>digitales</span>
          </span>
        </div>

        {/* Indicador de conectividad */}
        <div className="flex items-center gap-2">
          <div
            className={`h-2 w-2 rounded-full ${
              isOnline ? 'bg-green-500' : 'bg-red-500'
            }`}
          />
          <span className="text-sm text-gray-600">
            {isOnline ? 'En línea' : 'Sin conexión'}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="flex items-center justify-end gap-2">
            <p className="text-sm font-medium text-gray-900">
              {effectiveSession?.user?.nombre ? capitalize(effectiveSession.user.nombre) : 'Usuario'}
            </p>
            {effectiveSession?.hasPremiumAccess && (
              <button
                onClick={goToPremium}
                className="flex items-center gap-1.5 px-4 py-1.5 badge-premium rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
                title="Ver mi suscripción Premium"
              >
                <FaCrown className="text-sm" />
                <span>Premium</span>
              </button>
            )}
          </div>
          {effectiveSession?.almacen?.nombre && (
            <p className="text-xs text-gray-500">
              {capitalize(effectiveSession.almacen.nombre)}
              {/* Negocios con varias sucursales bajo el mismo RUT suelen repetir
                  el mismo nombre en todas — mostrar la dirección es lo que
                  realmente permite confirmar en qué sucursal está la sesión. */}
              {effectiveSession.almacen.direccion && ` · ${effectiveSession.almacen.direccion}`}
            </p>
          )}
        </div>

        {!isDesktopApp && (
          <>
            <button
              onClick={() => navigate('/descargar')}
              className="hidden items-center gap-2 rounded-lg bg-[#FF6B00] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#FF4A00] lg:flex"
              title="Descarga la app de escritorio"
            >
              <FaWindows />
              Descarga la versión escritorio
            </button>
          </>
        )}

        <Button
          variant="danger"
          size="sm"
          onClick={() => setShowLogoutModal(true)}
          isLoading={isPending}
          title="Cerrar sesión"
        >
          <FaPowerOff />
        </Button>
      </div>

      {/* Modal de confirmación de logout */}
      {showLogoutModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLogoutModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#11194F' }}>
              ¿Estás seguro?
            </h2>
            <p className="text-gray-700 mb-6">
              ¿Deseas cerrar sesión y salir de la aplicación?
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-6 py-2 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowLogoutModal(false);
                  handleLogout();
                }}
                disabled={isPending}
                className="px-6 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50 hover:opacity-90"
                style={{ backgroundColor: '#EF4444' }}
              >
                {isPending ? 'Cerrando...' : 'Sí, cerrar sesión'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </header>
  );
};
